/* eslint-disable import/max-dependencies -- add-visit wizard orchestrates case, visit, and case-details flows across several hooks */
import { isDefined } from "@clipboard-health/util-ts";
import { useIsModificationReasonSatisfied } from "@src/appV2/ExperimentalHomeHealth/components/ModificationReasonField";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { getCaseQueryKey, useCase } from "../api/useCase";
import { useCreateVisit } from "../api/useCreateVisit";
import {
  useParseDateFieldToISO,
  useFormatWorkplaceDateInput,
} from "../api/useFormatTimestampForWorkplace";
import { useUpdateCase } from "../api/useUpdateCase";
import { useCanSetRates } from "../../lib/useCanSetRates";
import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { useLogHomeHealthEvent } from "../../lib/useLogHomeHealthEvent";
import { useRateConfiguration } from "../../lib/useRateConfiguration";
import { isCaseDescriptionValid } from "../caseDescriptionValidation";
import { getHomeHealthCaseMobileDetailPath } from "../paths";
import { isHourlyPricingAllowed, PricingType, VisitType, WorkerRequirements } from "../types";
import { buildCreateVisitAttributes } from "./careTeam/buildCreateVisitAttributes";
import { type CaseDetailsData } from "./careTeam/MobileCaseDetailsFields";
import {
  createVisitDraft,
  DEFAULT_VISIT_DRAFT_FIELDS,
  type VisitDraft,
  type VisitDraftFieldValue,
  type VisitDraftFields,
} from "./careTeam/visitDraft";
import { type CareTeamWizardScreen, MobileCareTeamWizardView } from "./MobileCareTeamWizardView";
import { getMobileCaseStep2FieldErrors } from "./mobileCaseStep2Validation";

// The visit being configured. `id: null` = a new visit; an id = editing an
// existing draft (committed only on save).
interface VisitEditorState {
  id: string | null;
  step: "discipline" | "schedule";
  fields: VisitDraftFields;
}

const DEFAULT_CASE_DETAILS: CaseDetailsData = {
  specialties: [],
  description: "",
  modificationReason: "",
};

function pluralizeVisits(count: number): string {
  return `${count} ${count === 1 ? "visit" : "visits"}`;
}

export function MobileAddVisitWizardContainer() {
  const { caseId } = useParams<{ caseId: string }>();
  const history = useHistory();
  const workplaceId = useCurrentUserId() ?? "";
  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const logHomeHealthEvent = useLogHomeHealthEvent();

  const { data: caseItem } = useCase(caseId);
  const patientAddress = caseItem?.attributes.patient.attributes.formattedAddress ?? "";

  // Start in the editor for the first visit rather than an empty care-team list.
  const [phase, setPhase] = useState<"careTeam" | "details">("careTeam");
  const [drafts, setDrafts] = useState<VisitDraft[]>([]);
  const [editor, setEditor] = useState<VisitEditorState | null>(() => ({
    id: null,
    step: "discipline",
    fields: DEFAULT_VISIT_DRAFT_FIELDS,
  }));
  const [caseDetails, setCaseDetails] = useState<CaseDetailsData>(DEFAULT_CASE_DETAILS);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // Seed the case-details step with the existing case once it loads. Without
  // this, the description/specialties fields start empty, so the case-details
  // submit would overwrite the stored values with blanks — and the new
  // description-minimum gate would force re-authoring a description the case
  // already has. Modification reason is intentionally left blank: it must
  // describe this specific change. Runs once so it never stomps user edits.
  const hasHydratedCaseDetailsRef = useRef(false);
  useEffect(() => {
    if (hasHydratedCaseDetailsRef.current || !isDefined(caseItem)) {
      return;
    }

    hasHydratedCaseDetailsRef.current = true;
    setCaseDetails((previous) => ({
      ...previous,
      specialties: caseItem.attributes.specialties,
      description: caseItem.attributes.description,
    }));
  }, [caseItem]);

  const { mutateAsync: createVisit, isLoading: isCreatingVisit } = useCreateVisit();
  const { mutateAsync: updateCase, isLoading: isUpdatingCase } = useUpdateCase(caseId);
  const isSubmitting = isCreatingVisit || isUpdatingCase;

  // Idempotency guards so a retry after a partial failure doesn't re-update the
  // case or re-post visits that already succeeded.
  const caseUpdatedRef = useRef(false);
  const createdDraftIds = useRef<Set<string>>(new Set());

  const canSetRates = useCanSetRates();
  const isModificationReasonSatisfied = useIsModificationReasonSatisfied(
    caseDetails.modificationReason
  );
  const parseDateFieldToISO = useParseDateFieldToISO();
  const formatWorkplaceDateInput = useFormatWorkplaceDateInput();
  const todayIso = formatWorkplaceDateInput(new Date().toISOString());

  // Rate configuration for the visit currently being edited.
  const rates = useRateConfiguration({
    visitType: (editor?.fields.visitType as VisitType) || undefined,
    workerRequirements: (editor?.fields.workerRequirement as WorkerRequirements) || undefined,
    pricingType: editor?.fields.pricingType ?? PricingType.PER_VISIT,
    patientAddress,
  });
  const editorFields = editor?.fields ?? DEFAULT_VISIT_DRAFT_FIELDS;
  const scheduleErrors = getMobileCaseStep2FieldErrors({
    data: editorFields,
    rates,
    pricingType: editorFields.pricingType,
    todayIso,
  });

  function handleEditorFieldChange(field: keyof VisitDraftFields, value: VisitDraftFieldValue) {
    setEditor((prev) => {
      if (!prev) {
        return prev;
      }

      const fields: VisitDraftFields = { ...prev.fields, [field]: value };
      // Hourly is only valid for REGULAR non-PT visits; reset it when no longer allowed.
      if (
        (field === "visitType" || field === "workerRequirement") &&
        !isHourlyPricingAllowed({
          visitType: fields.visitType as VisitType,
          workerReq: fields.workerRequirement as WorkerRequirements,
        })
      ) {
        fields.pricingType = PricingType.PER_VISIT;
      }

      return { ...prev, fields };
    });
  }

  function isDisciplineValid(fields: VisitDraftFields): boolean {
    return Boolean(fields.visitType) && Boolean(fields.workerRequirement);
  }

  function isScheduleValid(fields: VisitDraftFields): boolean {
    const isRegularVisit = fields.visitType === VisitType.REGULAR;
    const hasSchedule = isRegularVisit
      ? Boolean(fields.visitsPerWeek) && Boolean(fields.totalWeeks)
      : true;
    const hasRates = canSetRates
      ? Number.isFinite(Number.parseFloat(fields.chargePerVisit)) &&
        Number.isFinite(Number.parseFloat(fields.payPerVisit))
      : true;
    const hasEstimatedDuration =
      fields.pricingType === PricingType.PER_HOUR
        ? Number.parseFloat(fields.estimatedDuration) >= 2
        : true;
    return (
      Boolean(fields.firstVisitDueDate) &&
      hasSchedule &&
      hasRates &&
      hasEstimatedDuration &&
      Object.keys(scheduleErrors).length === 0
    );
  }

  function openNewVisitEditor() {
    setEditor({ id: null, step: "discipline", fields: DEFAULT_VISIT_DRAFT_FIELDS });
  }

  function openEditVisitEditor(draftId: string) {
    const draft = drafts.find((item) => item.id === draftId);
    if (!draft) {
      return;
    }

    const { id: _id, ...fields } = draft;
    setEditor({ id: draftId, step: "discipline", fields });
  }

  function commitEditor() {
    if (!editor) {
      return;
    }

    const { id, fields } = editor;
    setDrafts((prev) =>
      id === null
        ? [...prev, createVisitDraft(fields)]
        : prev.map((draft) => (draft.id === id ? { ...draft, ...fields } : draft))
    );
    setEditor(null);
  }

  function removeDraft(draftId: string) {
    setDrafts((prev) => prev.filter((draft) => draft.id !== draftId));
  }

  function handleCaseDetailsChange(field: keyof CaseDetailsData, value: string | string[]) {
    setCaseDetails((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit() {
    setSubmitError(null);
    const { specialties, description, modificationReason } = caseDetails;

    try {
      if (!caseUpdatedRef.current) {
        await updateCase({
          data: { attributes: { specialties, description, modificationReason }, type: "case" },
        });
        caseUpdatedRef.current = true;
      }
    } catch {
      setSubmitError("Something went wrong updating the case. Please try again.");
      return;
    }

    const failedDraftIds: string[] = [];
    for (const draft of drafts) {
      if (createdDraftIds.current.has(draft.id)) {
        continue;
      }

      try {
        // eslint-disable-next-line no-await-in-loop -- sequential so the created-set stays consistent on retry
        const visit = await createVisit({
          data: {
            attributes: buildCreateVisitAttributes({
              draft,
              caseId,
              modificationReason,
              canSetRates,
              toDeadlineIso: parseDateFieldToISO,
            }),
            type: "visit",
          },
        });
        createdDraftIds.current.add(draft.id);
        logHomeHealthEvent(APP_V2_USER_EVENTS.HOME_HEALTH_VISIT_CREATED_AFTER_CASE, {
          workplaceId,
          workerReq: draft.workerRequirement || undefined,
          type: draft.visitType,
          visitId: visit.id,
          caseId,
          patientId: caseItem?.attributes.patient.id,
          performedByAdmin: isAdmin,
        });
      } catch {
        failedDraftIds.push(draft.id);
      }
    }

    await queryClient.invalidateQueries(getCaseQueryKey({ workplaceId, caseId }));

    if (failedDraftIds.length === 0) {
      setIsSuccess(true);
    } else {
      setSubmitError(
        `${pluralizeVisits(failedDraftIds.length)} couldn't be posted. Please try again.`
      );
    }
  }

  const screen: CareTeamWizardScreen = isSuccess
    ? "success"
    : phase === "details"
      ? "details"
      : editor !== null
        ? editor.step
        : "review";

  function handleBack() {
    if (phase === "details") {
      setPhase("careTeam");
    } else if (editor !== null && editor.step === "schedule") {
      setEditor((prev) => (prev ? { ...prev, step: "discipline" } : prev));
    } else if (editor !== null && drafts.length > 0) {
      // Cancel this visit's edit/add and return to the care-team list.
      setEditor(null);
    } else {
      // First visit (no drafts yet) — leave the flow entirely.
      history.goBack();
    }
  }

  function handlePrimary() {
    if (phase === "details") {
      void handleSubmit();
    } else if (editor !== null && editor.step === "discipline") {
      setEditor((prev) => (prev ? { ...prev, step: "schedule" } : prev));
    } else if (editor !== null) {
      commitEditor();
    } else {
      setPhase("details");
    }
  }

  const primaryLabel =
    phase === "details"
      ? `Post ${pluralizeVisits(drafts.length)}`
      : editor !== null && editor.step === "discipline"
        ? "Next"
        : editor !== null
          ? editor.id === null
            ? "Add visit"
            : "Save visit"
          : "Continue";

  const primaryDisabled =
    isSubmitting ||
    (phase === "details"
      ? !isModificationReasonSatisfied || !isCaseDescriptionValid(caseDetails.description)
      : editor !== null && editor.step === "discipline"
        ? !isDisciplineValid(editor.fields)
        : editor !== null
          ? !isScheduleValid(editor.fields)
          : drafts.length === 0);

  return (
    <MobileCareTeamWizardView
      title="Add visits"
      progressSteps={["visits", "details"]}
      activeStepIndex={phase === "details" ? 1 : 0}
      screen={screen}
      submitError={submitError}
      editorFields={editorFields}
      rates={rates}
      scheduleErrors={scheduleErrors}
      onEditorFieldChange={handleEditorFieldChange}
      caseDetails={caseDetails}
      onCaseDetailsChange={handleCaseDetailsChange}
      drafts={drafts}
      onAddAnother={openNewVisitEditor}
      onEditDraft={openEditVisitEditor}
      onRemoveDraft={removeDraft}
      successVisitCount={drafts.length}
      successVariant="visits"
      onDone={() => {
        history.replace(getHomeHealthCaseMobileDetailPath(caseId));
      }}
      back={{ label: "Back", disabled: isSubmitting, onClick: handleBack }}
      primary={{ label: primaryLabel, disabled: primaryDisabled, onClick: handlePrimary }}
    />
  );
}
